import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import "@/lib/i18n";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { PropertyProvider } from "@/contexts/PropertyContext";
import AppLayout from "@/components/layout/AppLayout";
import ProtectedRoute from "@/components/layout/ProtectedRoute";

import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import HotelSignup from "@/pages/HotelSignup";
import SecuritySettings from "@/pages/SecuritySettings";
import Dashboard from "@/pages/Dashboard";
import Reservations from "@/pages/Reservations";
import Rooms from "@/pages/Rooms";
import RoomTypes from "@/pages/RoomTypes";
import Guests from "@/pages/Guests";
import Housekeeping from "@/pages/Housekeeping";
import POS from "@/pages/POS";
import Channels from "@/pages/Channels";
import Reports from "@/pages/Reports";
import Properties from "@/pages/Properties";
import Staff from "@/pages/Staff";
import AuditLogs from "@/pages/AuditLogs";
import Settings from "@/pages/Settings";
import EmailSettings from "@/pages/EmailSettings";
import Restrictions from "@/pages/Restrictions";
import BillingPortal from "@/pages/BillingPortal";
import GuestDetail from "@/pages/GuestDetail";

import SuperOverview from "@/pages/superadmin/SuperOverview";
import SuperHotels from "@/pages/superadmin/SuperHotels";
import SuperHotelDetail from "@/pages/superadmin/SuperHotelDetail";
import SuperPlans from "@/pages/superadmin/SuperPlans";
import SuperSubscriptions from "@/pages/superadmin/SuperSubscriptions";
import SuperProperties from "@/pages/superadmin/SuperProperties";
import SuperUsers from "@/pages/superadmin/SuperUsers";
import SuperModules from "@/pages/superadmin/SuperModules";
import SuperInvoices from "@/pages/superadmin/SuperInvoices";
import { SuperPaymentGateways, SuperOTAs } from "@/pages/superadmin/IntegrationsPage";
import SuperAdminPaymentGateways from "@/pages/superadmin/SuperAdminPaymentGateways";
import SuperChannelManager from "@/pages/superadmin/SuperChannelManager";
import SuperChannex from "@/pages/superadmin/SuperChannex";
import SuperBookingEngine from "@/pages/superadmin/SuperBookingEngine";
import SuperTickets from "@/pages/superadmin/SuperTickets";
import SuperSystemLogs from "@/pages/superadmin/SuperSystemLogs";
import SuperFeatureFlags from "@/pages/superadmin/SuperFeatureFlags";
import SuperGlobalSettings from "@/pages/superadmin/SuperGlobalSettings";
import SuperReports from "@/pages/superadmin/SuperReports";
import SuperLanguages from "@/pages/superadmin/SuperLanguages";
import SuperEmailTemplates from "@/pages/superadmin/SuperEmailTemplates";
import SuperDemoRequests from "@/pages/superadmin/SuperDemoRequests";
import SuperSalesLeads from "@/pages/superadmin/SuperSalesLeads";
import SuperDeploy from "@/pages/superadmin/SuperDeploy";
import SuperBilling from "@/pages/superadmin/SuperBilling";
import SuperDunning from "@/pages/superadmin/SuperDunning";
import SuperOnboardingAnalytics from "@/pages/superadmin/SuperOnboardingAnalytics";
import SuperBackups from "@/pages/superadmin/SuperBackups";
import WizardList from "@/pages/wizard/WizardList";
import HotelWizard from "@/pages/wizard/HotelWizard";
import BookingEngine from "@/pages/BookingEngine";
import IntegrationHub from "@/pages/IntegrationHub";
import BookEmbed from "@/pages/BookEmbed";
import Billing from "@/pages/Billing";
import PublicPay from "@/pages/PublicPay";
import Revenue from "@/pages/Revenue";
import Inbox from "@/pages/Inbox";
import Integrations from "@/pages/Integrations";
import CheckIns from "@/pages/CheckIns";
import PublicCheckIn from "@/pages/PublicCheckIn";
import Loyalty from "@/pages/Loyalty";
import Surveys from "@/pages/Surveys";
import PublicSurvey from "@/pages/PublicSurvey";
import RatePlans from "@/pages/RatePlans";

function RootRedirect() {
    const { user, loading } = useAuth();
    // Subdomain / custom-domain routing: if the current host is a hotel
    // subdomain (e.g. holiday.akomoda.co) or a verified custom domain,
    // resolve it to a slug and forward straight to the booking engine.
    const [redirectSlug, setRedirectSlug] = React.useState(null);
    const [resolved, setResolved] = React.useState(false);
    React.useEffect(() => {
        const host = window.location.hostname.toLowerCase();
        const isReserved = host === "localhost"
            || host === "app.akomoda.co"
            || host === "akomoda.co"
            || host.endsWith(".emergentagent.com")
            || host.endsWith(".emergentcf.cloud")
            || host.endsWith(".preview.emergentagent.com");
        if (isReserved) { setResolved(true); return; }
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/integration/resolve-host?host=${encodeURIComponent(host)}`)
            .then((r) => r.ok ? r.json() : null)
            .then((data) => {
                if (data && data.slug) setRedirectSlug(data.slug);
                setResolved(true);
            })
            .catch(() => setResolved(true));
    }, []);
    if (redirectSlug) return <Navigate to={`/book/${redirectSlug}`} replace />;
    if (!resolved) return null;
    if (loading) return null;
    if (!user) return <Navigate to="/login" replace />;
    return <Navigate to={user.role === "super_admin" ? "/admin" : "/dashboard"} replace />;
}

export default function App() {
    return (
        <AuthProvider>
            <PropertyProvider>
                <BrowserRouter>
                    <Toaster position="top-right" toastOptions={{ className: "text-sm" }} />
                    <Routes>
                        <Route path="/login" element={<Login />} />
                        <Route path="/akomoadm" element={<Login />} />
                        <Route path="/signup" element={<Signup />} />
                        <Route path="/register" element={<HotelSignup />} />
                        <Route path="/forgot-password" element={<Login />} />
                        <Route path="/reset-password" element={<Login />} />
                        <Route path="/book/:hotelId" element={<BookEmbed />} />
                        <Route path="/pay/:token" element={<PublicPay />} />
                        <Route path="/check-in/:token" element={<PublicCheckIn />} />
                        <Route path="/s/:token" element={<PublicSurvey />} />
                        <Route path="/" element={<RootRedirect />} />
                        <Route
                            element={
                                <ProtectedRoute>
                                    <AppLayout />
                                </ProtectedRoute>
                            }
                        >
                            {/* Hotel */}
                            <Route path="/dashboard" element={<Dashboard />} />
                            <Route path="/reservations" element={<Reservations />} />
                            <Route path="/rooms" element={<Rooms />} />
                            <Route path="/room-types" element={<ProtectedRoute roles={["hotel_admin", "super_admin"]}><RoomTypes /></ProtectedRoute>} />
                            <Route path="/guests" element={<Guests />} />
                            <Route path="/guests/:id" element={<GuestDetail />} />
                            <Route path="/housekeeping" element={<Housekeeping />} />
                            <Route path="/pos" element={<POS />} />
                            <Route path="/billing" element={<ProtectedRoute roles={["hotel_admin", "property_manager", "finance_staff"]}><Billing /></ProtectedRoute>} />
                            <Route path="/revenue" element={<ProtectedRoute roles={["hotel_admin", "property_manager"]}><Revenue /></ProtectedRoute>} />
                            <Route path="/inbox" element={<Inbox />} />
                            <Route path="/check-ins" element={<ProtectedRoute roles={["hotel_admin", "property_manager", "reception"]}><CheckIns /></ProtectedRoute>} />
                            <Route path="/integrations" element={<ProtectedRoute roles={["hotel_admin", "property_manager"]}><Integrations /></ProtectedRoute>} />
                            <Route path="/loyalty" element={<ProtectedRoute roles={["hotel_admin", "property_manager", "reception"]}><Loyalty /></ProtectedRoute>} />
                            <Route path="/surveys" element={<ProtectedRoute roles={["hotel_admin", "property_manager", "reception"]}><Surveys /></ProtectedRoute>} />
                            <Route path="/channels" element={<Channels />} />
                            <Route path="/rate-plans" element={<ProtectedRoute roles={["hotel_admin", "property_manager"]}><RatePlans /></ProtectedRoute>} />
                            <Route path="/booking-engine" element={<ProtectedRoute roles={["hotel_admin", "property_manager"]}><BookingEngine /></ProtectedRoute>} />
                            <Route path="/website-booking" element={<ProtectedRoute roles={["hotel_admin", "property_manager"]}><IntegrationHub /></ProtectedRoute>} />
                            <Route path="/reports" element={<Reports />} />
                            <Route path="/properties" element={<Properties />} />
                            <Route path="/staff" element={<Staff />} />
                            <Route path="/audit" element={<AuditLogs />} />
                            <Route path="/settings" element={<Settings />} />
                            <Route path="/settings/email" element={<ProtectedRoute roles={["super_admin", "hotel_admin", "property_manager"]}><EmailSettings /></ProtectedRoute>} />
                            <Route path="/restrictions" element={<ProtectedRoute roles={["super_admin", "hotel_admin", "property_manager", "revenue_manager"]}><Restrictions /></ProtectedRoute>} />
                            <Route path="/settings/subscription" element={<ProtectedRoute roles={["hotel_admin", "property_manager", "finance_staff"]}><BillingPortal /></ProtectedRoute>} />
                            <Route path="/settings/billing" element={<ProtectedRoute roles={["hotel_admin", "property_manager", "finance_staff"]}><BillingPortal /></ProtectedRoute>} />
                            <Route path="/security" element={<SecuritySettings />} />

                            {/* Super admin */}
                            <Route path="/admin" element={<ProtectedRoute roles={["super_admin"]}><SuperOverview /></ProtectedRoute>} />
                            <Route path="/admin/hotels" element={<ProtectedRoute roles={["super_admin"]}><SuperHotels /></ProtectedRoute>} />
                            <Route path="/admin/hotels/:hotelId" element={<ProtectedRoute roles={["super_admin"]}><SuperHotelDetail /></ProtectedRoute>} />
                            <Route path="/admin/properties" element={<ProtectedRoute roles={["super_admin"]}><SuperProperties /></ProtectedRoute>} />
                            <Route path="/admin/users" element={<ProtectedRoute roles={["super_admin"]}><SuperUsers /></ProtectedRoute>} />
                            <Route path="/admin/modules" element={<ProtectedRoute roles={["super_admin"]}><SuperModules /></ProtectedRoute>} />
                            <Route path="/admin/plans" element={<ProtectedRoute roles={["super_admin"]}><SuperPlans /></ProtectedRoute>} />
                            <Route path="/admin/subscriptions" element={<ProtectedRoute roles={["super_admin"]}><SuperSubscriptions /></ProtectedRoute>} />
                            <Route path="/admin/invoices" element={<ProtectedRoute roles={["super_admin"]}><SuperInvoices /></ProtectedRoute>} />
                            <Route path="/admin/gateways" element={<ProtectedRoute roles={["super_admin"]}><SuperAdminPaymentGateways /></ProtectedRoute>} />
                            <Route path="/admin/otas" element={<ProtectedRoute roles={["super_admin"]}><SuperOTAs /></ProtectedRoute>} />
                            <Route path="/admin/channel-manager" element={<ProtectedRoute roles={["super_admin"]}><SuperChannelManager /></ProtectedRoute>} />
                            <Route path="/admin/distribution" element={<ProtectedRoute roles={["super_admin"]}><SuperChannex /></ProtectedRoute>} />
                            <Route path="/admin/booking-engine" element={<ProtectedRoute roles={["super_admin"]}><SuperBookingEngine /></ProtectedRoute>} />
                            <Route path="/admin/tickets" element={<ProtectedRoute roles={["super_admin"]}><SuperTickets /></ProtectedRoute>} />
                            <Route path="/admin/system-logs" element={<ProtectedRoute roles={["super_admin"]}><SuperSystemLogs /></ProtectedRoute>} />
                            <Route path="/admin/flags" element={<ProtectedRoute roles={["super_admin"]}><SuperFeatureFlags /></ProtectedRoute>} />
                            <Route path="/admin/global-settings" element={<ProtectedRoute roles={["super_admin"]}><SuperGlobalSettings /></ProtectedRoute>} />
                            <Route path="/admin/reports" element={<ProtectedRoute roles={["super_admin"]}><SuperReports /></ProtectedRoute>} />
                            <Route path="/admin/languages" element={<ProtectedRoute roles={["super_admin"]}><SuperLanguages /></ProtectedRoute>} />
                            <Route path="/admin/email-templates" element={<ProtectedRoute roles={["super_admin"]}><SuperEmailTemplates /></ProtectedRoute>} />
                            <Route path="/admin/demo-requests" element={<ProtectedRoute roles={["super_admin"]}><SuperDemoRequests /></ProtectedRoute>} />
                            <Route path="/admin/sales-leads" element={<ProtectedRoute roles={["super_admin"]}><SuperSalesLeads /></ProtectedRoute>} />
                            <Route path="/admin/deploy" element={<ProtectedRoute roles={["super_admin"]}><SuperDeploy /></ProtectedRoute>} />
                            <Route path="/admin/billing" element={<ProtectedRoute roles={["super_admin"]}><SuperBilling defaultTab="dashboard" /></ProtectedRoute>} />
                            <Route path="/admin/billing/pricing" element={<ProtectedRoute roles={["super_admin"]}><SuperBilling defaultTab="pricing" /></ProtectedRoute>} />
                            <Route path="/admin/billing/modules" element={<ProtectedRoute roles={["super_admin"]}><SuperBilling defaultTab="modules" /></ProtectedRoute>} />
                            <Route path="/admin/billing/plans" element={<ProtectedRoute roles={["super_admin"]}><SuperBilling defaultTab="plans" /></ProtectedRoute>} />
                            <Route path="/admin/billing/subscriptions" element={<ProtectedRoute roles={["super_admin"]}><SuperBilling defaultTab="subscriptions" /></ProtectedRoute>} />
                            <Route path="/admin/billing/invoices" element={<ProtectedRoute roles={["super_admin"]}><SuperBilling defaultTab="invoices" /></ProtectedRoute>} />
                            <Route path="/admin/billing/audit" element={<ProtectedRoute roles={["super_admin"]}><SuperBilling defaultTab="audit" /></ProtectedRoute>} />
                            <Route path="/admin/dunning" element={<ProtectedRoute roles={["super_admin"]}><SuperDunning /></ProtectedRoute>} />
                            <Route path="/admin/onboarding-analytics" element={<ProtectedRoute roles={["super_admin"]}><SuperOnboardingAnalytics /></ProtectedRoute>} />
                            <Route path="/admin/backups" element={<ProtectedRoute roles={["super_admin"]}><SuperBackups /></ProtectedRoute>} />
                            <Route path="/wizard" element={<ProtectedRoute roles={["super_admin", "hotel_admin"]}><WizardList /></ProtectedRoute>} />
                            <Route path="/wizard/:id" element={<ProtectedRoute roles={["super_admin", "hotel_admin"]}><HotelWizard /></ProtectedRoute>} />
                        </Route>
                        <Route path="*" element={<RootRedirect />} />
                    </Routes>
                </BrowserRouter>
            </PropertyProvider>
        </AuthProvider>
    );
}
